/**
 * Support tickets — the platform owner's inbox, working like the Driveline
 * enquiries inbox: filter by state, read what led to the ticket, reply by
 * email, mark it off.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, MessageSquare, Monitor, Building2, Reply } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { GuideAnalytics } from './GuideAnalytics';
import { GuideGaps } from './GuideGaps';
import { SavedAnswers } from './SavedAnswers';
import { OnboardingProgress } from './OnboardingProgress';

const STATES = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'open', label: 'Open' },
  { key: 'answered', label: 'Answered' },
  { key: 'closed', label: 'Closed' },
];

const TONE = {
  new: 'bg-teal-50 text-teal-700 border-teal-200',
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  answered: 'bg-sky-50 text-sky-700 border-sky-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const SupportTickets = () => {
  const { authHeader, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [state, setState] = useState('all');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState('');
  const [draft, setDraft] = useState({});
  const [sending, setSending] = useState('');
  const [teachDraft, setTeachDraft] = useState({});
  const [taughtCount, setTaughtCount] = useState(0);

  const load = useCallback(async (s) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/platform/support-tickets`, {
        params: { status: s }, headers: authHeader(),
      });
      setRows(data.tickets || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load tickets');
    } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { if (user?.is_platform_owner) load(state); }, [load, state, user]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/platform/support-tickets/${id}`, { status },
        { headers: authHeader() });
      toast.success(`Marked ${status}`);
      await load(state);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  // Answering from here rather than switching to email: the reply still
  // reaches the workshop by email, since they have no ticket screen of their
  // own, but the thread stays on the ticket.
  const sendReply = async (id, close) => {
    const message = (draft[id] || '').trim();
    if (message.length < 5) return;
    setSending(id);
    const teach = teachDraft[id];
    try {
      await axios.post(`${API}/platform/support-tickets/${id}/reply`, {
        message,
        close,
        // Curated reuse: only what you tick, in the words you chose.
        save_answer: Boolean(teach),
        answer_question: teach?.question || '',
        answer_text: teach?.answer || '',
      }, { headers: authHeader() });
      toast.success(teach
        ? (close ? 'Replied, closed, and the assistant learned it' : 'Reply sent — the assistant learned it')
        : (close ? 'Replied and closed' : 'Reply sent'));
      setDraft((d) => ({ ...d, [id]: '' }));
      setTeachDraft((t) => ({ ...t, [id]: null }));
      if (teach) setTaughtCount((n) => n + 1);
      await load(state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send that reply');
    } finally { setSending(''); }
  };

  const toggleTeach = (t) => setTeachDraft((prev) => ({
    ...prev,
    [t.id]: prev[t.id] ? null : { question: t.subject, answer: draft[t.id] || '' },
  }));

  // Tickets are the platform owner's, not every admin's — send anyone else to
  // their own console rather than showing a shell that reads as an empty inbox.
  if (user && !user.is_platform_owner) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6" data-testid="support-tickets-page">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Support tickets</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Questions the help panel could not resolve on its own.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATES.map((s) => (
          <button
            key={s.key}
            onClick={() => setState(s.key)}
            className={`text-xs font-semibold rounded-full border px-3 py-1.5 ${
              state === s.key ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-600 hover:border-slate-400'
            }`}
            data-testid={`ticket-filter-${s.key}`}
          >
            {s.label}
            {s.key !== 'all' && counts[s.key] ? ` (${counts[s.key]})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-slate-300" size={20} /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="tickets-empty">
          No tickets here. Workshops are finding their answers in the panel.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`ticket-${t.reference}`}>
              <button
                onClick={() => setOpenId(openId === t.id ? '' : t.id)}
                className="w-full text-left px-4 py-3"
                data-testid={`ticket-toggle-${t.reference}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${TONE[t.status]}`}>
                    {t.status}
                  </span>
                  <span className="font-mono text-[11px] text-slate-400">{t.reference}</span>
                  <span className="text-sm font-semibold text-slate-900">{t.subject}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><Building2 size={11} /> {t.workshop_name || '—'} · {t.plan || 'no plan'}</span>
                  <span className="inline-flex items-center gap-1"><Mail size={11} /> {t.raised_by}</span>
                  {t.screen && <span className="inline-flex items-center gap-1"><Monitor size={11} /> {t.screen}</span>}
                  <span>{new Date(t.created_at).toLocaleString('en-IN')}</span>
                </div>
              </button>

              {openId === t.id && (
                <div className="border-t border-slate-100 px-4 py-3" data-testid={`ticket-body-${t.reference}`}>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.message}</p>

                  {(t.transcript || []).length > 0 && (
                    <div className="mt-4">
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        <MessageSquare size={11} /> What they asked the assistant
                      </div>
                      <div className="mt-2 space-y-1.5 border-l-2 border-slate-100 pl-3">
                        {t.transcript.map((m, i) => (
                          <p key={i} className="text-xs text-slate-600">
                            <span className="font-semibold text-slate-400">
                              {m.role === 'user' ? 'Them: ' : 'Assistant: '}
                            </span>
                            {m.content}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-4">
                    <a
                      href={`mailto:${t.raised_by}?subject=${encodeURIComponent(`Re: ${t.subject} (${t.reference})`)}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 hover:border-slate-400 text-slate-700 rounded-lg px-3 py-2"
                      data-testid={`ticket-reply-${t.reference}`}
                    >
                      <Mail size={13} /> Open in email
                    </a>
                    {['open', 'answered', 'closed'].filter((s) => s !== t.status).map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(t.id, s)}
                        className="text-xs font-semibold border border-slate-300 hover:border-slate-400 text-slate-700 rounded-lg px-3 py-2 capitalize"
                        data-testid={`ticket-mark-${s}-${t.reference}`}
                      >
                        Mark {s}
                      </button>
                    ))}
                  </div>

                  {(t.attachments || []).length > 0 && (
                    <div className="mt-4" data-testid={`ticket-shots-${t.reference}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        Screenshots they sent
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {t.attachments.map((a) => (
                          <a
                            key={a.id}
                            href={`${API}/help/attachments/${a.id}`}
                            target="_blank"
                            rel="noreferrer"
                            title={a.filename}
                            data-testid={`ticket-shot-${a.id}`}
                          >
                            <img
                              src={`${API}/help/attachments/${a.id}`}
                              alt={a.filename}
                              className="h-28 w-40 object-cover rounded-lg border border-slate-200 hover:border-teal-400"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {(t.thread || []).length > 0 && (
                    <div className="mt-4" data-testid={`ticket-replies-${t.reference}`}>
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        <Reply size={11} /> Conversation
                      </div>
                      <div className="mt-2 space-y-2">
                        {t.thread.map((m) => (
                          <div
                            key={m.id}
                            className={m.author === 'workshop'
                              ? 'bg-amber-50 border border-amber-200 rounded-lg px-3 py-2'
                              : 'bg-slate-50 border border-slate-200 rounded-lg px-3 py-2'}
                          >
                            <p className="text-xs text-slate-700 whitespace-pre-wrap">{m.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {m.author === 'workshop' ? 'Workshop' : 'You'} · {m.by} ·{' '}
                              {new Date(m.created_at).toLocaleString('en-IN')}
                              {m.delivered ? ' · emailed' : ' · not emailed'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                      Reply to {t.raised_by}
                    </label>
                    <textarea
                      rows={4}
                      value={draft[t.id] || ''}
                      onChange={(e) => setDraft((d) => ({ ...d, [t.id]: e.target.value }))}
                      placeholder="Answer them here — it goes to their email as it is."
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      data-testid={`ticket-reply-input-${t.reference}`}
                    />

                    <label className="mt-2 flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(teachDraft[t.id])}
                        onChange={() => toggleTeach(t)}
                        className="mt-0.5"
                        data-testid={`ticket-teach-${t.reference}`}
                      />
                      <span>
                        Teach the assistant this answer
                        <span className="block text-[10px] text-slate-400">
                          Every workshop will read it, so take their specifics out first.
                        </span>
                      </span>
                    </label>

                    {teachDraft[t.id] && (
                      <div className="mt-2 border border-teal-200 bg-teal-50/60 rounded-lg p-3 space-y-2" data-testid={`ticket-teach-form-${t.reference}`}>
                        <div>
                          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            The question, in general terms
                          </label>
                          <input
                            value={teachDraft[t.id].question}
                            onChange={(e) => setTeachDraft((d) => ({ ...d, [t.id]: { ...d[t.id], question: e.target.value } }))}
                            className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5"
                            data-testid={`ticket-teach-question-${t.reference}`}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            The answer the assistant should give
                          </label>
                          <textarea
                            rows={3}
                            value={teachDraft[t.id].answer || draft[t.id] || ''}
                            onChange={(e) => setTeachDraft((d) => ({ ...d, [t.id]: { ...d[t.id], answer: e.target.value } }))}
                            placeholder="Defaults to your reply above."
                            className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 resize-none"
                            data-testid={`ticket-teach-answer-${t.reference}`}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button
                        onClick={() => sendReply(t.id, false)}
                        disabled={sending === t.id || (draft[t.id] || '').trim().length < 5}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white rounded-lg px-3 py-2"
                        data-testid={`ticket-send-reply-${t.reference}`}
                      >
                        {sending === t.id ? <Loader2 className="animate-spin" size={13} /> : <Reply size={13} />}
                        Send reply
                      </button>
                      <button
                        onClick={() => sendReply(t.id, true)}
                        disabled={sending === t.id || (draft[t.id] || '').trim().length < 5}
                        className="text-xs font-semibold border border-slate-300 hover:border-slate-400 disabled:opacity-50 text-slate-700 rounded-lg px-3 py-2"
                        data-testid={`ticket-reply-close-${t.reference}`}
                      >
                        Reply and close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <GuideAnalytics />
      <GuideGaps />
      <SavedAnswers refreshKey={taughtCount} />
      <OnboardingProgress />
    </div>
  );
};

export default SupportTickets;
